import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';

import {
  formatiereCode,
  type AktivierungsAnzeige,
  type FehlerGrund,
  type GeraeteAktivierung,
} from './geraeteaktivierung';
import { useScanSchutz } from '../scan-schutz';
import { aktivierungsQrSvg } from './qr';
import './ersteinrichtung.css';

/**
 * Ersteinrichtung an der Kasse (Geräteaktivierung V2), nach den freigegebenen Figma-Rahmen
 * EA-1 … EA-6d, EA-T*, EA-S* (Section `401:19556`, freigegeben 2026-11-02).
 *
 * Zeigt nur, was der Automat liefert: Benutzercode, QR-Inhalt (Adresse des Backoffice, nie ein
 * Schlüssel), Bestätigungscode, Unternehmen/Kasse. Gerätegeheimnis und Kassenschlüssel kommen in
 * der Anzeige nicht vor (`AktivierungsAnzeige`), daher auch nicht in Bildschirmaufnahmen.
 */
export interface ErsteinrichtungProps {
  aktivierung: GeraeteAktivierung;
  /** Netzstatus des Geräts (navigator.onLine bzw. Kopfzeile der App). */
  online: boolean;
  /** „Erweiterte Einrichtung“: bisherige Einrichtung mit Kassen-ID und Kassenschlüssel. */
  onErweitert: () => void;
  /** „Weiter zur Mitarbeiteranmeldung“ nach erfolgreicher Aktivierung. */
  onWeiter: () => void;
  /** `window.BilanxGeraet` der Android-Hülle (für den Bildschirmschutz); sonst undefined. */
  bilanxGeraet?: unknown;
  /** Für Tests: Uhr und QR-Erzeugung. */
  jetzt?: () => number;
  qrSvg?: (inhalt: string) => Promise<string | null>;
}

type Ansicht = 'qr' | 'code';

function useAnzeige(a: GeraeteAktivierung): AktivierungsAnzeige {
  return useSyncExternalStore(
    (f) => a.abonniere(f),
    () => a.aktuell(),
  );
}

/** Sekundentakt nur, solange eine Frist sichtbar ist. */
function useJetzt(jetzt: () => number, aktiv: boolean): number {
  const [t, setT] = useState(jetzt);
  useEffect(() => {
    if (!aktiv) {
      return;
    }
    setT(jetzt());
    const id = setInterval(() => {
      setT(jetzt());
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [jetzt, aktiv]);
  return t;
}

/**
 * SUNMI-Hülle: FLAG_SECURE (keine Bildschirmaufnahme/Vorschau) nur, solange Benutzer- oder
 * Bestätigungscode sichtbar ist (EA-2/EA-3). Nur, wenn die Bridge die Methode anbietet.
 */
function useBildschirmschutz(bilanxGeraet: unknown, an: boolean): void {
  useEffect(() => {
    const g = bilanxGeraet as
      | { bildschirmschutz?: (o: { an: boolean }) => Promise<unknown> | undefined }
      | null
      | undefined;
    const schutz = g?.bildschirmschutz;
    if (typeof schutz !== 'function' || !an) {
      return;
    }
    const rufe = (wert: boolean): void => {
      try {
        void Promise.resolve(schutz.call(g, { an: wert })).catch(() => undefined);
      } catch {
        // Bridge-Fehler ändern den Ablauf nicht
      }
    };
    rufe(true);
    return () => {
      rufe(false);
    };
  }, [bilanxGeraet, an]);
}

function uhrzeit(ms: number): string {
  return new Date(ms).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}

function rest(bis: number, jetzt: number): string {
  const s = Math.max(0, Math.ceil((bis - jetzt) / 1000));
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, '0')}`;
}

function Kopf(props: { online: boolean }) {
  return (
    <div className="ea-kopf">
      <div className="ea-wortmarke" aria-label="Bilanx Kassa">
        <span className="ea-marke" aria-hidden="true">
          BX
        </span>
        <span className="ea-wort" aria-hidden="true">
          <b>BILANX</b>
          <span>KASSA</span>
        </span>
      </div>
      <span className={`status-pille ${props.online ? 'online' : 'offline'}`}>
        <i aria-hidden="true" />
        {props.online ? 'Online' : 'Offline'}
      </span>
    </div>
  );
}

function Hinweis(props: {
  art: 'info' | 'warnung' | 'gefahr' | 'erfolg';
  titel: string;
  text: string;
}) {
  return (
    <div className={`ea-hinweis ea-hinweis-${props.art}`} role="note">
      <b>{props.titel}</b>
      <span>{props.text}</span>
    </div>
  );
}

function Tasten(props: { children: ReactNode }) {
  return <div className="ea-tasten">{props.children}</div>;
}

function Warten(props: { text: string }) {
  return (
    <p className="ea-warten" role="status">
      <span className="ea-kreisel" aria-hidden="true" />
      {props.text}
    </p>
  );
}

function Frist(props: { bis: number; jetzt: number; nurZiffern?: boolean }) {
  return (
    <p className="ea-frist">
      Gültig bis {uhrzeit(props.bis)} Uhr · noch {rest(props.bis, props.jetzt)} min · einmal
      verwendbar{props.nurZiffern === true ? ' · nur Ziffern' : ''}
    </p>
  );
}

function QrBild(props: { inhalt: string; erzeuge: (inhalt: string) => Promise<string | null> }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let aktiv = true;
    setSvg(null);
    props
      .erzeuge(props.inhalt)
      .then((s) => {
        if (aktiv) {
          setSvg(s);
        }
      })
      .catch(() => {
        if (aktiv) {
          setSvg(null);
        }
      });
    return () => {
      aktiv = false;
    };
  }, [props.inhalt, props.erzeuge]);
  if (svg === null) {
    return <div className="ea-qr ea-qr-leer" aria-hidden="true" />;
  }
  // SVG stammt aus der Bibliothek qrcode (nur Pfade), Inhalt ist die geprüfte https-Adresse
  return (
    <div
      className="ea-qr"
      role="img"
      aria-label="QR-Code zur Aktivierung"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

const FEHLER_ANSICHT: Record<
  FehlerGrund,
  {
    symbol: string;
    ton: 'warnung' | 'gefahr' | 'neutral';
    titel: string;
    text: string;
    weiter: string;
    hinweis?: { art: 'gefahr' | 'info'; titel: string; text: string };
  }
> = {
  abgelaufen: {
    symbol: '!',
    ton: 'warnung',
    titel: 'Aktivierungscode abgelaufen',
    text: 'Der Code war 10 Minuten gültig. Fordern Sie einen neuen Code an – auf diesem Gerät wurde nichts verändert.',
    weiter: 'Neuen Code anfordern',
  },
  abgebrochen: {
    symbol: '×',
    ton: 'gefahr',
    titel: 'Aktivierung abgebrochen',
    text: 'Die Aktivierung wurde im Backoffice abgebrochen. Auf diesem Gerät wurde nichts verändert.',
    weiter: 'Neu starten',
  },
  gesperrt: {
    symbol: '⊘',
    ton: 'gefahr',
    titel: 'Aktivierung gesperrt',
    text: 'Der Bestätigungscode wurde zu oft falsch eingegeben. Aus Sicherheitsgründen ist diese Aktivierung gesperrt.',
    weiter: 'Neuen Code anfordern',
    hinweis: {
      art: 'gefahr',
      titel: 'Niemand aktiviert gerade dieses Gerät?',
      text: 'Dann Ihren Administrator informieren – jemand könnte versucht haben, ein fremdes Gerät zu verbinden.',
    },
  },
  offline: {
    symbol: '⌁',
    ton: 'neutral',
    titel: 'Keine Verbindung',
    text: 'Für die Aktivierung braucht dieses Gerät eine Internetverbindung. Bitte WLAN oder Netzwerkkabel prüfen.',
    weiter: 'Erneut versuchen',
    hinweis: {
      art: 'info',
      titel: 'Offline verkaufen geht erst nach der Aktivierung',
      text: 'Ein noch nicht aktiviertes Gerät kann keine Belege erstellen.',
    },
  },
  zuVieleVersuche: {
    symbol: '!',
    ton: 'warnung',
    titel: 'Zu viele Versuche',
    text: 'Zu viele Aktivierungsversuche von diesem Anschluss. Bitte in einigen Minuten erneut versuchen.',
    weiter: 'Erneut versuchen',
  },
  speicher: {
    symbol: '!',
    ton: 'gefahr',
    titel: 'Zugang nicht gespeichert',
    text: 'Der Zugang konnte auf diesem Gerät nicht sicher gespeichert werden. Bitte erneut versuchen; bleibt der Fehler, den Support verständigen.',
    weiter: 'Erneut versuchen',
  },
  fehler: {
    symbol: '!',
    ton: 'gefahr',
    titel: 'Aktivierung fehlgeschlagen',
    text: 'Die Aktivierung ist fehlgeschlagen. Auf diesem Gerät wurde nichts verändert. Bitte erneut versuchen.',
    weiter: 'Erneut versuchen',
  },
};

export function Ersteinrichtung(props: ErsteinrichtungProps) {
  const { aktivierung } = props;
  const a = useAnzeige(aktivierung);
  const [ansicht, setAnsicht] = useState<Ansicht>('qr');
  const jetztFn = props.jetzt ?? Date.now;
  const jetzt = useJetzt(jetztFn, a.art === 'wartet' || a.art === 'zuordnung');
  const qrSvg = props.qrSvg ?? aktivierungsQrSvg;
  useBildschirmschutz(props.bilanxGeraet, a.art === 'wartet' || a.art === 'zuordnung');
  // Go-Live: das Enter eines Scans löst keine Taste aus (z. B. „Abbrechen“); Hinweis bis zum
  // nächsten Schritt der Aktivierung
  const [scanHinweis, setScanHinweis] = useState<string | null>(null);
  useScanSchutz(setScanHinweis);
  useEffect(() => {
    setScanHinweis(null);
  }, [a.art]);

  // EA-2c/EA-6d: Server nicht erreichbar zählt wie offline, auch wenn das Netz „online“ meldet
  const verbindungOk =
    props.online &&
    !('verbindung' in a && a.verbindung === 'unterbrochen') &&
    !(a.art === 'fehlgeschlagen' && a.grund === 'offline');

  function starte(mit: Ansicht): void {
    setAnsicht(mit);
    void aktivierung.starte();
  }
  function abbrechen(): void {
    void aktivierung.abbrechen();
  }

  let inhalt: ReactNode;
  switch (a.art) {
    case 'nichtAktiviert':
    case 'startet': {
      const startet = a.art === 'startet';
      inhalt = (
        <>
          <h1>Willkommen bei Bilanx Kassa</h1>
          <p className="ea-unterzeile">Dieses Gerät ist noch nicht aktiviert.</p>
          <p className="ea-text">
            Ein Administrator Ihres Unternehmens verbindet dieses Gerät im Backoffice unter
            „Geräte“. Kassen-ID, Kassenschlüssel oder Zugangsdaten brauchen Sie dafür nicht.
          </p>
          <div className="ea-wahl">
            <button
              type="button"
              className="taste gross"
              disabled={startet}
              onClick={() => {
                starte('qr');
              }}
            >
              Mit QR-Code verbinden
            </button>
            <button
              type="button"
              className="taste gross zweit"
              disabled={startet}
              onClick={() => {
                starte('code');
              }}
            >
              Mit Aktivierungscode verbinden
            </button>
          </div>
          {startet ? <Warten text="Aktivierung wird vorbereitet …" /> : null}
          <button type="button" className="ea-erweitert" onClick={props.onErweitert}>
            ▸ Erweiterte Einrichtung (bisheriger Kassenschlüssel, nur mit Support)
          </button>
        </>
      );
      break;
    }
    case 'wartet': {
      const unterbrochen = a.verbindung === 'unterbrochen';
      const mitQr = ansicht === 'qr' && a.qrInhalt !== '';
      const hinweis = unterbrochen ? (
        <Hinweis
          art="warnung"
          titel="Verbindung unterbrochen"
          text="Die Kasse versucht es automatisch weiter. Der Code bleibt gültig, bis er abläuft – nichts neu eingeben."
        />
      ) : null;
      inhalt = mitQr ? (
        <>
          <h1>Mit QR-Code verbinden</h1>
          <p className="ea-text">
            Den QR-Code mit dem Smartphone oder Tablet eines Administrators scannen – oder den Code
            im Backoffice unter „Geräte › Gerät hinzufügen“ eingeben.
          </p>
          <div className="ea-qr-zeile">
            <QrBild inhalt={a.qrInhalt} erzeuge={qrSvg} />
            <div>
              <p className="ea-etikett">Aktivierungscode</p>
              <p
                className="ea-code"
                aria-label={`Aktivierungscode ${a.benutzerCode.split('').join(' ')}`}
              >
                {formatiereCode(a.benutzerCode)}
              </p>
              <Frist bis={a.gueltigBis} jetzt={jetzt} />
            </div>
          </div>
          {hinweis}
          {unterbrochen ? null : <Warten text="Wartet auf einen Administrator …" />}
          <Tasten>
            <button type="button" className="taste zweit" onClick={abbrechen}>
              Abbrechen
            </button>
            <button
              type="button"
              className="taste zweit"
              onClick={() => {
                void aktivierung.starte();
              }}
            >
              Neuen Code anfordern
            </button>
          </Tasten>
        </>
      ) : (
        <>
          <h1>Mit Aktivierungscode verbinden</h1>
          <p className="ea-text">
            Diesen Code einem Administrator nennen. Er gibt ihn im Backoffice unter „Geräte › Gerät
            hinzufügen“ ein.
          </p>
          <p
            className="ea-zellen"
            aria-label={`Aktivierungscode ${a.benutzerCode.split('').join(' ')}`}
          >
            {a.benutzerCode.split('').map((z, i) => (
              <span key={i} className={i === 4 ? 'ea-zelle ea-trenner-vor' : 'ea-zelle'}>
                {z}
              </span>
            ))}
          </p>
          <Frist bis={a.gueltigBis} jetzt={jetzt} nurZiffern />
          {hinweis ?? (
            <Hinweis
              art="info"
              titel="Code nur an Ihren Administrator weitergeben"
              text="Bilanx fragt nie am Telefon oder per E-Mail nach diesem Code. Der Code ist nach 10 Minuten oder nach der Verwendung ungültig."
            />
          )}
          {unterbrochen ? null : <Warten text="Wartet auf einen Administrator …" />}
          <Tasten>
            <button type="button" className="taste zweit" onClick={abbrechen}>
              Abbrechen
            </button>
            {a.qrInhalt === '' ? null : (
              <button
                type="button"
                className="taste zweit"
                onClick={() => {
                  setAnsicht('qr');
                }}
              >
                QR-Code anzeigen
              </button>
            )}
          </Tasten>
        </>
      );
      break;
    }
    case 'zuordnung':
      inhalt = (
        <>
          <h1>Bestätigungscode</h1>
          <p className="ea-text">
            Ein Administrator hat dieses Gerät zugeordnet. Er gibt diesen Code im Backoffice ein, um
            die Aktivierung zu bestätigen.
          </p>
          <div className="ea-ziel">
            <b>{a.unternehmerName}</b>
            <span>{a.kassenName}</span>
          </div>
          <p
            className="ea-code ea-code-gross"
            aria-label={`Bestätigungscode ${a.bestaetigungsCode.split('').join(' ')}`}
          >
            {formatiereCode(a.bestaetigungsCode)}
          </p>
          <p className="ea-frist">6 Ziffern · nach 5 falschen Eingaben gesperrt</p>
          <Hinweis
            art="warnung"
            titel="Unternehmen oder Kasse stimmen nicht?"
            text="Dann den Code nicht weitergeben und „Abbrechen“ wählen. Richtig zugeordnet wird nur, was Sie hier sehen."
          />
          {a.verbindung === 'unterbrochen' ? (
            <Hinweis
              art="warnung"
              titel="Verbindung unterbrochen"
              text="Die Kasse versucht es automatisch weiter."
            />
          ) : (
            <Warten text="Wartet auf die Bestätigung …" />
          )}
          <Tasten>
            <button type="button" className="taste zweit" onClick={abbrechen}>
              Abbrechen
            </button>
          </Tasten>
        </>
      );
      break;
    case 'laeuft':
      inhalt = (
        <>
          <span className="ea-kreisel ea-kreisel-gross" aria-hidden="true" />
          <h1>Aktivierung läuft …</h1>
          <p className="ea-text">
            {a.kassenName !== null && a.unternehmerName !== null
              ? `Dieses Gerät wird mit ${a.kassenName} von ${a.unternehmerName} verbunden. `
              : 'Dieses Gerät wird verbunden. '}
            Bitte das Gerät nicht ausschalten.
          </p>
          <ol className="ea-schritte" role="status">
            <li className="fertig">Gerät zugeordnet</li>
            <li className="fertig">Aktivierung bestätigt</li>
            <li className="aktiv">Zugang wird sicher auf diesem Gerät gespeichert</li>
            <li>Einstellungen der Kasse werden geladen</li>
          </ol>
          {a.verbindung === 'unterbrochen' ? (
            <Hinweis
              art="warnung"
              titel="Verbindung unterbrochen"
              text="Die Kasse versucht es automatisch weiter – bitte das Gerät eingeschaltet lassen."
            />
          ) : null}
        </>
      );
      break;
    case 'erfolgreich':
      inhalt = (
        <>
          <span className="ea-symbol ea-symbol-erfolg" aria-hidden="true">
            ✓
          </span>
          <h1>Gerät erfolgreich aktiviert</h1>
          <p className="ea-text">
            Dieses Gerät ist jetzt eingerichtet. Ab sofort meldet es sich nach jedem Neustart
            automatisch an.
          </p>
          <dl className="ea-fakten">
            <dt>Unternehmen</dt>
            <dd>{a.zugang.unternehmerName}</dd>
            <dt>Kasse</dt>
            <dd>{a.zugang.kasseName === '' ? a.zugang.kassenId : a.zugang.kasseName}</dd>
          </dl>
          <div className="ea-wahl">
            <button type="button" className="taste gross" onClick={props.onWeiter}>
              Weiter zur Mitarbeiteranmeldung
            </button>
          </div>
        </>
      );
      break;
    case 'fehlgeschlagen': {
      const f = FEHLER_ANSICHT[a.grund];
      inhalt = (
        <>
          <span className={`ea-symbol ea-symbol-${f.ton}`} aria-hidden="true">
            {f.symbol}
          </span>
          <h1>{f.titel}</h1>
          <p className="ea-text">{f.text}</p>
          {f.hinweis === undefined ? null : (
            <Hinweis art={f.hinweis.art} titel={f.hinweis.titel} text={f.hinweis.text} />
          )}
          <Tasten>
            <button type="button" className="taste zweit" onClick={abbrechen}>
              Zurück
            </button>
            <button
              type="button"
              className="taste"
              onClick={() => {
                void aktivierung.starte();
              }}
            >
              {f.weiter}
            </button>
          </Tasten>
        </>
      );
      break;
    }
  }

  return (
    <main className="ea-flaeche">
      <section className="ea-karte" aria-live="polite">
        <Kopf online={verbindungOk} />
        {scanHinweis !== null && (
          <Hinweis art="warnung" titel="Scan ignoriert" text={scanHinweis} />
        )}
        {inhalt}
      </section>
    </main>
  );
}
